import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { Button, Col, Divider, Form, Row, Select, Table } from 'antd';
import Input from 'antd/es/input/Input';
import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { useApp } from '@/App';
import { SearchMKLParam, useMKLSearch } from '@/libs/fleet-api/services/mkl';
import { useForm } from '@/libs/antd/form';
import { FileSearchOutlined } from '@ant-design/icons';

const MKLListPage: FC = () => {
	const { navigate } = useApp();

	const [searchParam, setSearchParam] = useState<SearchMKLParam>({
		pagination: {
			pageNumber: 1,
			pageSize: 10,
		},
	});

	const searchQuery = useMKLSearch(searchParam);
	const isLoading = searchQuery.isFetching;

	const dataSource =
		searchQuery.data?.data?.items?.map((item, index) => ({
			...item,
			key: index + 1,
		})) ?? [];

	const { form, fieldName } = useForm<{
		searchBy?: string;
		keyword?: string;
	}>();

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			<FleetTitle>Memo Kredit Limit</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ flex: 'none', style: { width: 150 } }}
				onFinish={(values) => {
					setSearchParam({
						pagination: { pageNumber: 1, pageSize: 10 },
						search:
							values.keyword && values.searchBy
								? { by: values.searchBy, value: values.keyword }
								: undefined,
					});
				}}
			>
				<Row gutter={20}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchBy')}
							initialValue="spf-no"
							label="Kata Kunci"
						>
							<Select
								options={[
									{ label: 'No SPF', value: 'spf-no' },
									{ label: 'Nama Customer', value: 'customer-name' },
								]}
								style={{ width: 200 }}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item name={fieldName('keyword')}>
							<Input placeholder="Search" disabled={isLoading} />
						</Form.Item>
					</Col>
					<Col flex="none" style={{ width: 190 }}>
						<Button
							block
							type="primary"
							htmlType="submit"
							disabled={isLoading}
						>
							SEARCH
						</Button>
					</Col>
				</Row>
			</Form>

			<Divider style={{ margin: 0 }} />

			<FleetTitle level={3} marginBottom={0}>
				Informasi Limit Pembiayaan
			</FleetTitle>

			<Table
				pagination={{
					total: searchQuery.data?.data.totalCount,
					pageSize: searchParam.pagination?.pageSize ?? 10,
					current: searchParam.pagination?.pageNumber ?? 1,
					onChange: (pageNumber) => {
						setSearchParam((prev) => ({
							...prev,
							pagination: {
								pageNumber,
								pageSize: prev.pagination.pageSize,
							},
						}));
					},
				}}
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={dataSource}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						render: (_, __, index) =>
							index +
							1 +
							searchParam.pagination.pageSize *
							(searchParam.pagination.pageNumber - 1),
					},
					{
						title: 'No.SPF',
						render: (_, record) =>
							record.spfNo ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('SPFPage', {
											spfId: record.spfId,
											napResolutionId: 0,
											onBack: () => navigate('MKLListPage'),
											viewOnly: true,
										});
									}}
								>
									{record.spfNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'Nama Customer',
						render: (_, record) => record.customerName || '-',
					},
					{
						title: 'Group BMPP',
						render: (_, record) =>
							record.groupBmppName ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('FRMCustomerGroupListPage');
									}}
								>
									{record.groupBmppName}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'Tanggal Expired',
						render: (_, record) =>
							record.expiredDate
								? dayjs(record.expiredDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'Status',
						render: (_, record) => {
							const isAktif =
								record.expiredDate &&
								!dayjs(record.expiredDate).isBefore(dayjs(), 'day');
							return isAktif ? 'Aktif' : 'Tidak Aktif';
						},
					},
					{
						title: 'View',
						align: 'center',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									navigate('MKLPage', {
										mklId: record.spfId,
										onBack: () => navigate('MKLListPage'),
									});
								}}
							>
								<FileSearchOutlined style={{ fontSize: 18, color: '#1677ff' }} />
							</a>
						),
					},
				]}
			/>
		</div>
	);
};

export default MKLListPage;
